import { useEffect, useState } from "react";
import api from "../lib/api";
import { Activity, AlertCircle, ArrowRight, Loader2 } from "lucide-react";

const COLORS = {
  green: { bg: "bg-emerald-500", ring: "ring-emerald-200", chip: "bg-emerald-100 text-emerald-700" },
  amber: { bg: "bg-amber-500", ring: "ring-amber-200", chip: "bg-amber-100 text-amber-700" },
  red: { bg: "bg-red-500", ring: "ring-red-200", chip: "bg-red-100 text-red-700" },
};

export default function ConferenceHealthCard({ eventId, eventTitle, refreshIntervalMs = 60000 }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const fetchHealth = async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const r = await api.get(`/health/conference/${eventId}`);
        if (!cancelled) {
          setHealth(r.data);
          setLastUpdated(new Date());
        }
      } catch {
        // Silently keep last-known good health; surface failure via stale timestamp
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    fetchHealth();
    const interval = setInterval(() => fetchHealth(true), refreshIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [eventId, refreshIntervalMs]);

  if (!eventId) return null;
  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <Loader2 className="h-5 w-5 text-[#0F4C81] animate-spin" />
      </div>
    );
  }
  if (!health) return null;

  const palette = COLORS[health.color] || COLORS.amber;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="health-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold inline-flex items-center gap-1.5">
            <Activity className={`h-3 w-3 ${refreshing ? "animate-pulse" : ""}`} /> Conference Health Score
            {refreshing && <span className="text-[9px] tracking-normal text-emerald-500 ml-1">· Updating</span>}
          </p>
          <h3 className="mt-1 text-base font-semibold tracking-tight text-[#0A192F]">{eventTitle || health.event_title}</h3>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full ${palette.chip}`} data-testid="health-rating">
          {health.rating}
        </span>
      </div>

      {/* Score gauge */}
      <div className="mt-5 flex items-center gap-5">
        <div className={`relative h-24 w-24 rounded-full ring-4 ${palette.ring} flex items-center justify-center`}>
          <div className="absolute inset-1 rounded-full bg-slate-50" />
          <div className="relative text-center">
            <p className="font-serif text-3xl text-[#0A192F]" data-testid="health-score">{health.score}</p>
            <p className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold">/ 100</p>
          </div>
        </div>
        <div className="flex-1 space-y-1.5 text-xs">
          <Component label="Payment" value={health.components.payment_rate} />
          <Component label="Schedule" value={health.components.schedule_adherence} />
          <Component label="LMS" value={health.components.lms_completion} />
          <Component label="Voting" value={health.components.voting_completion} />
          <Component label="Tickets (urgent)" value={health.components.urgent_tickets} unit="" inverse />
        </div>
      </div>

      {/* Top risks */}
      {health.top_risks.length > 0 && (
        <div className="mt-5 pt-5 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold inline-flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3 text-amber-500" /> Top risks
          </p>
          <div className="mt-2 space-y-1.5">
            {health.top_risks.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-[#0A192F]">{r.area}</span>
                <span className="font-mono text-slate-500">{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended actions */}
      {health.recommended_actions.length > 0 && (
        <div className="mt-5 pt-5 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Recommended actions</p>
          <ul className="mt-2 space-y-1.5">
            {health.recommended_actions.map((a, i) => (
              <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
                <ArrowRight className="h-3.5 w-3.5 text-[#0F4C81] mt-0.5 flex-shrink-0" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Last updated */}
      {lastUpdated && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live · auto-refreshes every 60s
          </span>
          <span data-testid="health-last-updated">
            Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        </div>
      )}
    </div>
  );
}

function Component({ label, value, unit = "%", inverse = false }) {
  const v = value || 0;
  const display = `${v}${unit}`;
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 text-[10px] uppercase tracking-[0.18em] font-bold w-32 flex-shrink-0 leading-snug break-words">{label}</span>
      <div className="flex-1 min-w-0 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${inverse
            ? (v > 0 ? "bg-red-500" : "bg-emerald-500")
            : (v >= 80 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-red-500")}`}
          style={{ width: inverse ? `${Math.min(100, v * 10)}%` : `${Math.min(100, v)}%` }}
        />
      </div>
      <span className="text-slate-700 font-mono text-xs w-10 text-right tabular-nums shrink-0">{display}</span>
    </div>
  );
}
